import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../components/Form';
import { fetchBootstrap, hydrateSwrFromBootstrap } from '../lib/bootstrap';

interface Tenant {
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  role: 'OWNER' | 'MANAGER' | 'STAFF';
}

interface UserTenantsResponse {
  success: boolean;
  data: {
    user_id: string;
    tenants: Tenant[];
  };
  timestamp: string;
}

/**
 * 租户选择页面
 * 当用户有多个租户时，显示此页面让用户选择要使用的租户
 */
export function TenantSelect() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSelecting, setIsSelecting] = useState(false);
  /** 避免 Strict Mode 下 useEffect 跑兩次導致「只有一個租戶」時 toast 出現兩次 */
  const hasAutoSelectedRef = useRef(false);

  useEffect(() => {
    // 從 URL 參數中獲取 token 並保存到 localStorage（如果有的話）
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    if (tokenFromUrl) {
      localStorage.setItem('tenant_token', tokenFromUrl);
      localStorage.removeItem('staff_session_token');
      localStorage.removeItem('staff_id');
      localStorage.removeItem('staff_name');
      window.dispatchEvent(new Event('localStorageChange'));
      // 清除 URL 參數中的 token（避免洩露）
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('token');
      window.history.replaceState({}, '', newUrl.toString());
    }

    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      const apiBase =
        import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
      const token = localStorage.getItem('tenant_token');

      if (!token) {
        toast.error('請先登入');
        window.location.href = '/';
        return;
      }

      const response = await fetch(`${apiBase}/api/auth/user/tenants`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          toast.error('登入已過期，請重新登入');
          localStorage.removeItem('tenant_token');
          localStorage.removeItem('tenant_id');
          window.location.href = '/';
          return;
        }
        throw new Error('取得租戶列表失敗');
      }

      const result: UserTenantsResponse = await response.json();

      if (result.success && result.data && result.data.tenants) {
        setTenants(result.data.tenants);

        // 如果只有一个租户，直接选择（只執行一次，避免 Strict Mode 雙跑造成兩次 toast）
        if (result.data.tenants.length === 1) {
          if (hasAutoSelectedRef.current) return;
          hasAutoSelectedRef.current = true;
          handleSelectTenant(result.data.tenants[0].tenant_id);
          return;
        }

        // 如果没有租户，重定向到注册页面
        if (result.data.tenants.length === 0) {
          toast.error('您還沒有加入任何商家，請先註冊');
          window.location.href = '/tenant-register';
          return;
        }
      } else {
        throw new Error('取得租戶列表失敗');
      }
    } catch (error) {
      console.error('載入租戶列表失敗:', error);
      toast.error(
        error instanceof Error ? error.message : '載入租戶列表時發生錯誤'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectTenant = async (tenantId: string) => {
    if (isSelecting) return;

    setIsSelecting(true);
    try {
      const tenantToken = localStorage.getItem('tenant_token');

      // 設置當前租戶 ID
      localStorage.setItem('tenant_id', tenantId);

      // 觸發自定義事件，通知其他組件 localStorage 已變化
      window.dispatchEvent(new Event('localStorageChange'));

      // 等待一下，確保 localStorage 事件被其他組件接收
      await new Promise(resolve => setTimeout(resolve, 200));

      // 呼叫 Bootstrap API 一次取得 userTenants、tenant、featureFlags、member，並寫入 SWR 快取
      if (tenantToken) {
        try {
          const bootstrap = await fetchBootstrap(tenantToken, tenantId);
          if (bootstrap.success && bootstrap.data) {
            hydrateSwrFromBootstrap(bootstrap.data, tenantId, tenantToken);
          }
        } catch (bootstrapErr) {
          // Bootstrap 失敗不阻擋導向，後續各頁會 fallback 到個別 API
          console.warn('Bootstrap 載入失敗，將使用個別 API:', bootstrapErr);
        }
      }

      // 自動註冊裝置（Member 登入時）
      if (tenantToken) {
        const { autoRegisterDevice } = await import('../utils/deviceToken.js');
        await autoRegisterDevice(tenantId, tenantToken);
      }

      toast.success('已選擇商家', {
        duration: 2000,
      });

      // 跳轉到設定頁面
      setTimeout(() => {
        window.location.replace('/admin?tab=tenant-settings');
      }, 1000);
    } catch (error) {
      console.error('選擇租戶失敗:', error);
      toast.error('選擇租戶時發生錯誤');
      setIsSelecting(false);
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'OWNER':
        return '擁有者';
      case 'MANAGER':
        return '管理員';
      case 'STAFF':
        return '工作人員';
      default:
        return role;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'OWNER':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'MANAGER':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'STAFF':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">選擇商家</h1>
            <p className="text-gray-600">請選擇您要使用的商家</p>
          </div>

          {tenants.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-6">您還沒有加入任何商家</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="primary"
                  onClick={() => {
                    window.location.href = '/tenant-register';
                  }}
                  className="flex-1 sm:flex-none"
                >
                  創建新商家
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    window.location.href = '/invite/accept';
                  }}
                  className="flex-1 sm:flex-none"
                >
                  加入現有商家
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {tenants.map(tenant => (
                <button
                  key={tenant.tenant_id}
                  onClick={() => handleSelectTenant(tenant.tenant_id)}
                  disabled={isSelecting}
                  className="w-full p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900 mb-1">
                        {tenant.tenant_name}
                      </h3>
                      <p className="text-sm text-gray-500 mb-2">
                        {tenant.tenant_slug}
                      </p>
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium border ${getRoleColor(tenant.role)}`}
                        >
                          {getRoleLabel(tenant.role)}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <svg
                        className="w-6 h-6 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {isSelecting && (
            <div className="mt-6 text-center">
              <div className="inline-flex items-center gap-2 text-blue-600">
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span>正在切換商家...</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
